import React, { useEffect, useState, useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel } from "@tanstack/react-table";
import { Check, ChevronLeft, ChevronRight, Pencil, Save, Search, X } from "lucide-react";
import { useUser } from "@/app/context/UserContext";
import { chatbotProjectDatacenter, chatbotProjectDatacenterRow } from "@/app/services/chatbotProjectDatacenter";
import { iProjectDatacenterStoragePag, iProjectDatacenterStorageRow } from "@/app/models/interfaceDatacenterStorage";
import Loading from "../Elements/Loading";
import { createPortal } from "react-dom";

interface iEditing {
  row_id: string | null;
  key: string;
  value: string;
}

const RowEditExcel = ({ file_id }: { file_id: string }) => {
  const {  
      user, setUser, 
      userProject,setUserProject, 
      } = useUser();

  const [data, setData] = useState<Record<string, any>[]>([]);
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState(filter);

  const [isDataLoading, setIsDataLoading] = useState(false);
  //const [isSaveLoading, setIsSaveLoading] = useState(false);
 // const [loadingRows, setLoadingRows] = useState<{ [key: number]: boolean }>({});
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});


  const [editing, setEditing] = useState<iEditing>({
    row_id: null,
    key: "",
    value: "",
  });
  const [pendingChanges, setPendingChanges] = useState<iEditing[]>([]); 

  const [page, setPage] = useState(0);
  const pageSize = 10;

  const fetchDatacenterStorage = async () => { 
    setIsDataLoading(true); 
      try { 
        const request:iProjectDatacenterStoragePag = {
          datacenter_id: userProject.datacenter?.datacenter_id??"",
          file_id: file_id,
          query:filter,
          pageSize: pageSize,
          page: (page+1)
        };
        const response = await chatbotProjectDatacenter( request,user, setUser);   
        if(response.success && response.code == 200){
          setData(response.data as []); 
        }else {
          setData([]);
        } 
      } catch (error) { 
        setData([]);
      } finally { setIsDataLoading(false);}
  }; 

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilter(filter);
    }, 300);
    return () => clearTimeout(handler);
  }, [filter]);

  useEffect(() => { 
    setPage(0);
    fetchDatacenterStorage(); 
  }, [file_id,debouncedFilter]);

  useEffect(() => { 
    fetchDatacenterStorage(); 
  }, [page]);

  

  const startEditing = (row_id: string, key: string, value: any) => {
    setEditing({ row_id: row_id, key, value: value?.toString() || "" }); 
  };

  const saveEdit = () => {
    if (editing.row_id === null || !editing.key) return;
      
    setPendingChanges((prev) => [
      ...prev.filter((item) => !(item.row_id === editing.row_id && item.key === editing.key)),
      editing
    ]);    
    setEditing({ row_id: null, key: "", value: "" });
  };

  const cancelEdit = () => {
    setEditing({ row_id: null, key: "", value: "" });
  };

  const saveRow = async (row:any) => { 
      try { 
        let updatedRow = { ...row.original };
        setLoadingRows((prev) => ({ ...prev, [row.original.row_id]: true })); 

        if (pendingChanges.filter(O=>O.row_id==row.original.row_id).length) {
          pendingChanges.filter(item => item.row_id == updatedRow.row_id).forEach((obj) => {
            updatedRow[obj.key] = obj.value;
          });
        } 
        const request:iProjectDatacenterStorageRow = {
          datacenter_id: userProject.datacenter?.datacenter_id??"",
          file_id: file_id, 
          row_id: updatedRow.row_id,
          data:updatedRow 
        };
        const response = await chatbotProjectDatacenterRow( request,user, setUser);   
        if(response.success && response.code == 200){ 
          setData((prev) =>
            prev.map((item) =>
              item.row_id === updatedRow.row_id ? { ...item, ...updatedRow } : item
            )
          ); 
          // Limpiar los cambios pendientes 
          setPendingChanges((prev) => [ ...prev.filter((item) => item.row_id !== updatedRow.row_id) ]); 
        } 
      } catch (error) { 
        console.error("ERROR:",error)
      } finally {
        setLoadingRows((prev) => ({ ...prev, [row.original.row_id]: false }));  
      } 
       
  }; 

  const columns = useMemo(() => {
    if (data.length === 0) return [];
    const columnHelper = createColumnHelper<Record<string, any>>();

    return [
      ...Object.keys(data[0]).map((key) =>
        columnHelper.accessor(key, {
          header: key.charAt(0).toUpperCase() + key.slice(1),
          cell: ({ row }) => 
            editing.row_id === row.original.row_id && editing.key === key ? (
              <>
              <div 
                onClick={() => startEditing(row.original.row_id, key, row.original[key])} 
                className="cursor-pointer"
                > 
                {pendingChanges.find(O=>O.row_id == row.original.row_id && O.key === key)?.value || row.original[key]} 
              </div>
              {createPortal(
                <div className="fixed top-0 left-0 w-screen h-screen z-[1000] bg-black/50 flex items-center justify-center">
                  <div className="bg-white n-elevation-24 rounded-lg w-[40%] p-4 shadow-2xl">
                    <textarea
                      value={editing.value}
                      onChange={(e) => setEditing((prev) => ({ ...prev, value: e.target.value }))} 
                      className={`w-full p-2 border n-fmr-input border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700`}
                      rows={5}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-3">
                      <button 
                        onClick={cancelEdit}
                        className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
                      >
                        <X className="w-4 h-4 m-2" strokeWidth={1.5} />
                      </button> 
                      <button 
                        onClick={saveEdit}
                        className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
                      >
                        <Check className="w-4 h-4 m-2" strokeWidth={1.5} />
                      </button>  
                    </div>
                  </div>
                </div>
              ,document.body
              )}
              </>
            ) : (
              <div 
                onClick={() => startEditing(row.original.row_id, key, row.original[key])}
                className="relative group cursor-pointer w-full flex items-center gap-1"
              >
                {/* Ícono de lápiz encima del texto */}
                <Pencil 
                  className="absolute left-2 top-1 w-3 h-3 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150" 
                />
                <span className="truncate pl-6"> {/* Deja espacio para el ícono de lápiz */}
                  {pendingChanges.find(O => O.row_id === row.original.row_id && O.key === key)?.value || row.original[key]}
                </span>
              </div>
            )
            
            ,
          
          
        })
      ),
      
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) =>  
           
          pendingChanges.filter(O=>O.row_id === row.original.row_id).length ? (  
            <div className="flex gap-2"> 
              <button 
                type="button" 
                aria-label="Save" 
                onClick={() => saveRow(row)}
                className={`${loadingRows[row.original.row_id]?"":"btn-inca-secondary shadow-md rounded-full w-10 h-10 border border-gray-300"} `}
                disabled={loadingRows[row.original.row_id]}
              > 
                {loadingRows[row.original.row_id] ?
                  <Loading w="w-3" h="h-3" className="" />: 
                  <Save className="w-6 h-6 m-2" strokeWidth={1.5} />
                }
              </button> 
            </div> 
          ) :"" 
         ,
      }),
    ];
  }, [data, editing, pendingChanges, loadingRows]);

  const filteredData = useMemo(() => {
    const lowerFilter = filter.toLowerCase();
    return filter
      ? data.filter((row) =>
          Object.values(row).some((value) => value?.toString().toLowerCase().includes(lowerFilter))
        )
      : data;
  }, [data, filter]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * pageSize, (page + 1) * pageSize);
  }, [filteredData, page, pageSize]);

  const table = useReactTable({
    data,//: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return ( 
    <div className="w-full h-full ">
      <h2 className="text-lg font-bold mb-4">{userProject.datacenter?.files.find(item=>item.file_id === file_id)?.name}</h2>
      <div className="relative flex-1 mb-5">
        <input
          type="text"
          placeholder="Buscar..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full border rounded n-fmr-input-search border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
        /> 
        <Search className="absolute right-2.5 top-3.5 text-gray-400" size={18} />
      </div>

      <div className="overflow-y-auto">
        {isDataLoading?
          <div className="flex justify-center"> <Loading w="w-5" h="h-5" className="" /> </div>
          :
          <table className="w-full border-spacing-y-2 overflow-x-hidden scroll-custom">
            <thead className="n-elevation-1">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers
                    .filter((header) => String(header.column.columnDef.header) !== "Row_id")
                    .map((header) => (
                    <th key={header.id} className="p-2 text-left bg-gray-100 text-sm max-w-[200px] truncate whitespace-nowrap overflow-hidden">
                      {header.column.columnDef.header as string}
                    </th> 
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 border-b rounded-full ">
                  {row.getVisibleCells()
                          .filter((cell) => String(cell.column.columnDef.header) !== "Row_id")
                          .map((cell) => (

                    <td key={cell.id} className="p-2 cursor-pointer text-xs max-w-[200px] truncate whitespace-nowrap overflow-hidden">
                      {typeof cell.column.columnDef.cell === "function"
                        ? cell.column.columnDef.cell(cell.getContext())
                        : String(cell.getValue() ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        }
        
      </div>

      <div className="flex justify-end items-center mt-1"> 
            
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
              className={`p-1 border bg-slate-50 text-blue-500 rounded-full disabled:opacity-50 mr-2 shadow-lg 
                ${page > 0 ? "hover:shadow-xl hover:bg-white hover:border-blue-400  ":" text-slate-400"}
                `}
            >
                <ChevronLeft />
            </button>  
            <span className="px-4 py-2 bg-blue-700 text-white rounded-full disabled:opacity-50 mr-2">
                {page+1}
            </span> 
            <button
              onClick={() => setPage((prev) => ( prev + 1))}
              disabled={filteredData.length < (pageSize-1) }
              className={`p-1 border bg-slate-50 text-blue-500 rounded-full disabled:opacity-50 mr-2 shadow-lg 
                  ${filteredData.length <= (pageSize-1) ? "text-slate-400": "hover:shadow-xl hover:bg-white hover:border-blue-400 "}`}
            >
              <ChevronRight />
            </button>
        
      </div>  
    </div>
    
  );
};

export default RowEditExcel;
